import { useCallback, useEffect, useState } from 'react';
import { vorauszahlerAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Handshake, RefreshCw, Loader2, Phone, Check, X, Send, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/* Vorauszahler aus dem Bestand (FBC-Checkliste, 25.09.2026)
 *
 * Ziel: mindestens fünf Bestandsmitglieder zahlen ihre Restlaufzeit im Voraus.
 * Die Liste zeigt, wer in Frage kommt (12/24 Monate, Restlaufzeit ≥ 6 Monate,
 * noch kein Vorauszahler), den Vorschlag mit Rabatt und das Gesprächsergebnis.
 * Bei „angenommen“ legt das CRM eine Aufgabe für den Inhaber an — die
 * Abrechnung bleibt in Lexware.
 */

function euro(v) { return (v ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'; }
function datum(iso) { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return iso; } }

const STATUS = {
  angeboten: { label: 'Angeboten', cls: 'bg-amber-100 text-amber-700' },
  angenommen: { label: 'Angenommen', cls: 'bg-emerald-100 text-emerald-700' },
  abgelehnt: { label: 'Abgelehnt', cls: 'bg-muted text-muted-foreground' },
};

function Zeile({ k, gruende, onErgebnis }) {
  const [offen, setOffen] = useState(null);       // 'abgelehnt' -> Grund waehlen
  const [grund, setGrund] = useState('');
  const [notiz, setNotiz] = useState('');
  const [busy, setBusy] = useState(false);
  const v = k.vorschlag;
  const st = k.angebot?.status ? STATUS[k.angebot.status] : null;
  const fertig = k.angebot?.status === 'angenommen';

  const senden = async (status) => {
    if (status === 'abgelehnt' && !grund) { toast.error('Bitte den Grund wählen'); return; }
    setBusy(true);
    try {
      await onErgebnis(k.membership_id, { status, grund: status === 'abgelehnt' ? grund : null, notiz });
      setOffen(null); setGrund(''); setNotiz('');
    } finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-3 border-b border-border/50 last:border-b-0 space-y-2" data-testid={`vz-${k.membership_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[200px] flex-1">
          <div className="text-sm font-bold flex items-center gap-2">
            {k.name}
            {st && <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${st.cls}`}>{st.label}{k.angebot?.am ? ` · ${datum(k.angebot.am)}` : ''}</span>}
          </div>
          <div className="text-xs text-muted-foreground">
            {k.plan_name} · {k.duration_months} Monate · bis {datum(k.end_date)} · {euro(k.monthly_price)}/Monat
          </div>
          {k.angebot?.grund && <div className="text-[11px] text-muted-foreground">Grund: {gruende.find(g => g.grund === k.angebot.grund)?.label || k.angebot.grund}{k.angebot.notiz ? ` — ${k.angebot.notiz}` : ''}</div>}
        </div>
        <div className="text-right">
          <div className="text-sm font-extrabold tabular-nums">{euro(v.betrag)}</div>
          <div className="text-[10px] text-muted-foreground">{v.restmonate} Monate · statt {euro(v.regulaer)} · spart {euro(v.ersparnis)} ({Math.round(v.rabatt * 100)} %)</div>
        </div>
        {k.telefon && <a href={`tel:${k.telefon}`} className="text-xs font-mono text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{k.telefon}</a>}
        {!fertig && (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={() => senden('angeboten')} title="Angebot gemacht, Antwort steht aus"><Send className="w-3.5 h-3.5 mr-1" /> Angeboten</Button>
            <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => senden('angenommen')} data-testid={`vz-ja-${k.membership_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Angenommen</Button>
            <Button size="sm" variant="ghost" className="h-7 text-muted-foreground" disabled={busy} onClick={() => setOffen(offen === 'abgelehnt' ? null : 'abgelehnt')}><X className="w-3.5 h-3.5 mr-1" /> Abgelehnt</Button>
          </div>
        )}
      </div>
      {offen === 'abgelehnt' && (
        <div className="flex flex-wrap items-center gap-2 pl-1">
          <select value={grund} onChange={e => setGrund(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid={`vz-grund-${k.membership_id}`}>
            <option value="">Grund (Pflicht)…</option>
            {gruende.map(g => <option key={g.grund} value={g.grund}>{g.label}</option>)}
          </select>
          <Input value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="Notiz (optional)" className="h-8 w-56 text-xs" />
          <Button size="sm" variant="outline" className="h-8" disabled={busy || !grund} onClick={() => senden('abgelehnt')}>Speichern</Button>
        </div>
      )}
    </div>
  );
}

export default function VorauszahlerPage() {
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [filter, setFilter] = useState('offen');

  const laden = useCallback(async () => {
    setLaedt(true);
    try { const r = await vorauszahlerAPI.liste(); setDaten(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Liste konnte nicht geladen werden'); }
    setLaedt(false);
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const ergebnis = async (membershipId, body) => {
    try {
      const r = await vorauszahlerAPI.ergebnis(membershipId, body);
      toast.success(body.status === 'angenommen' ? 'Angenommen — Aufgabe für die Abrechnung in Lexware angelegt' : 'Gespeichert');
      if (r.data?.aufgabe_id === null && body.status === 'angenommen') toast.warning('Aufgabe konnte nicht angelegt werden — bitte den Inhaber direkt informieren');
      laden();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Konnte nicht gespeichert werden');
    }
  };

  if (laedt && !daten) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!daten) return null;
  const z = daten.zaehler;
  const liste = daten.kandidaten.filter(k => {
    const s = k.angebot?.status;
    if (filter === 'offen') return !s || s === 'angeboten';
    if (filter === 'alle') return true;
    return s === filter;
  });

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="vorauszahler-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Handshake className="w-6 h-6 text-marke" /> Vorauszahler aus dem Bestand</h1>
          <p className="text-sm text-muted-foreground">FBC-Ziel: mindestens {z.ziel} Bestandsmitglieder zahlen ihre Restlaufzeit im Voraus. Die Abrechnung bleibt in Lexware.</p>
        </div>
        <Button variant="outline" size="sm" onClick={laden}><RefreshCw className={`w-4 h-4 ${laedt ? 'animate-spin' : ''}`} /></Button>
      </div>

      <Card><CardContent className="p-4 space-y-2">
        <div className="flex items-end justify-between">
          <div><span className="text-3xl font-extrabold tabular-nums" data-testid="vz-zaehler">{z.angenommen}</span> <span className="text-muted-foreground text-sm">von {z.ziel} Vorauszahlern gewonnen</span></div>
          <span className={`text-sm font-bold ${z.angenommen >= z.ziel ? 'text-emerald-600' : 'text-muted-foreground'}`}>{z.fortschritt_prozent} %</span>
        </div>
        <div className="h-3 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full ${z.angenommen >= z.ziel ? 'bg-emerald-500' : 'bg-marke'}`} style={{ width: `${z.fortschritt_prozent}%` }} /></div>
        <div className="text-[11px] text-muted-foreground">
          {z.kandidaten} Kandidaten · {z.angeboten} angeboten · {z.abgelehnt} abgelehnt · nicht in der Liste: {z.schon_vorauszahler} schon Vorauszahler, {z.restlaufzeit_kurz} unter {daten.min_restmonate} Monaten Restlaufzeit, {z.gesperrt} gesperrt, {z.sondertarif} Sondertarif
        </div>
      </CardContent></Card>

      <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <Info className="w-4 h-4 flex-none mt-0.5" />
        <span>Vorschlag = Restmonate × Monatsbeitrag, abzüglich Vorauszahler-Rabatt der Laufzeit (10 % bei 12, 15 % bei 24 Monaten), wie im Verkaufsrechner. Ferien- und Event-Pauschale bleiben regulär. Bei „angenommen“ bekommt der Inhaber eine Aufgabe zur Abrechnung.</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {[['offen', 'Offen'], ['angenommen', 'Angenommen'], ['abgelehnt', 'Abgelehnt'], ['alle', 'Alle']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${filter === k ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:bg-muted/40'}`}>{l}</button>
        ))}
      </div>

      <Card><CardContent className="p-0">
        {liste.length === 0
          ? <div className="px-4 py-10 text-center text-sm text-muted-foreground">Niemand in dieser Auswahl.</div>
          : liste.map(k => <Zeile key={k.membership_id} k={k} gruende={daten.gruende} onErgebnis={ergebnis} />)}
      </CardContent></Card>
    </div>
  );
}
